import { type ComponentType, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { components, type GroupHeadingProps, type MenuProps, type OptionProps } from "react-select";
import { debounce } from "throttle-debounce";
import * as styles from "./styles.css";

export type Option<V = string | number> = {
  label: string;
  value: V;
};

export type GroupedOptions<T extends Option<string | number> = Option> = {
  label: string;
  options: T[];
};

export type AsyncSearchableSelectBoxProps<T extends Option<string | number> = Option> = {
  value: T | undefined;
  onChange: (option: T | undefined) => void;
  placeholder?: string;
  // 非同期でオプションを読み込む関数
  loadOptions: (inputValue: string) => Promise<readonly T[] | readonly GroupedOptions<T>[]>;
  noOptionsMessage?: string;
  customOption?: ComponentType<OptionProps<T, false, GroupedOptions<T>>>;
  customMenu?: ComponentType<MenuProps<T, false, GroupedOptions<T>>>;
  "aria-label"?: string;
  isDisabled?: boolean;
  instanceId?: string;
  // 初期表示用のデフォルトオプション
  defaultOptions?: readonly T[] | readonly GroupedOptions<T>[] | boolean;
  // debounce の遅延時間（ミリ秒）。0 の場合は debounce を無効化
  debounceMs?: number;
};

export const AsyncSearchableSelectBox = <T extends Option<string | number> = Option>({
  value,
  onChange,
  placeholder,
  loadOptions,
  noOptionsMessage,
  customOption,
  customMenu,
  "aria-label": ariaLabel,
  isDisabled,
  instanceId,
  defaultOptions = false,
  debounceMs = 0,
}: AsyncSearchableSelectBoxProps<T>) => {
  // debounce された loadOptions を作成
  const debouncedLoadOptions = useMemo(() => {
    // debounceMs が 0 の場合は debounce を無効化
    if (debounceMs === 0) {
      return loadOptions;
    }

    // 新しい debounce 関数を作成
    const debouncedFn = debounce(
      debounceMs,
      (
        inputValue: string,
        callback: (options: readonly T[] | readonly GroupedOptions<T>[]) => void,
      ) => {
        loadOptions(inputValue)
          .then(callback)
          .catch(() => {
            alert("検索中にエラーが発生しました");
            callback([]);
          });
      },
    );

    // Promise を返すラッパー関数
    return (inputValue: string): Promise<readonly T[] | readonly GroupedOptions<T>[]> => {
      return new Promise((resolve) => {
        debouncedFn(inputValue, resolve);
      });
    };
  }, [loadOptions, debounceMs]);

  return (
    <div className={styles.selectOuter}>
      <AsyncSelect<T, false, GroupedOptions<T>>
        value={value || null}
        loadOptions={debouncedLoadOptions}
        defaultOptions={defaultOptions}
        components={{
          Option: customOption ?? DefaultOption,
          GroupHeading,
          ...(customMenu && { Menu: customMenu }),
        }}
        className={styles.select}
        classNamePrefix="react-select"
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsMessage}
        isClearable
        onChange={(newValue) => onChange(newValue || undefined)}
        aria-label={ariaLabel}
        isDisabled={isDisabled}
        instanceId={instanceId}
        menuShouldScrollIntoView={false}
        // 非同期検索では、サーバー側でフィルタリングするため、クライアント側のフィルタリングは無効化
        filterOption={() => true}
        // キャッシュを無効化（常に新しいデータを取得）
        cacheOptions={false}
      />
    </div>
  );
};

const DefaultOption = <T extends Option<string | number>>(
  props: OptionProps<T, false, GroupedOptions<T>>,
) => {
  const {
    data: { label },
  } = props;
  return (
    <components.Option {...props}>
      <div className={styles.optionInner}>
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const GroupHeading = <T extends Option<string | number>>({
  data,
}: GroupHeadingProps<T, false, GroupedOptions<T>>) => {
  const { label } = data;
  return <div className={styles.categoryHeader}>▼{label}</div>;
};
